import { useCallback, useState } from 'react'
import type { Hex } from 'viem'
import { useAccount, usePublicClient, useWriteContract } from 'wagmi'
import { wnatAbi } from '../lib/abis'
import { WFLR_ADDRESS } from '../lib/tokens'

/**
 * FLR <-> WFLR, straight through WNat.
 *
 * The swap card's answer when the pair is FLR against WFLR. That is not a trade — it is 1:1,
 * there is nothing to route, and the API refuses to quote it (`SAME_TOKEN`) — so it never
 * goes near the router: `deposit` with the amount as value, or `withdraw(amount)`. One
 * transaction, no approval, and a fraction of what the same wrap costs routed through
 * `execute`.
 *
 * Every other pair involving FLR is a real swap and goes through `/quote` and `/swap`, where
 * the router wraps and unwraps inside the same transaction.
 */

export type WrapPhase =
  | { status: 'idle' }
  | { status: 'signing' }
  | { status: 'pending'; hash: Hex }
  | { status: 'success'; hash: Hex }
  | { status: 'error'; error: Error }

export function useWrap() {
  const { address } = useAccount()
  const publicClient = usePublicClient()
  const { writeContractAsync } = useWriteContract()
  const [phase, setPhase] = useState<WrapPhase>({ status: 'idle' })

  const execute = useCallback(
    async (direction: 'wrap' | 'unwrap', amount: bigint) => {
      if (!address) {
        setPhase({ status: 'error', error: new Error('connect a wallet first') })
        return
      }
      setPhase({ status: 'signing' })
      let hash: Hex
      try {
        hash = await writeContractAsync(
          direction === 'wrap'
            ? { address: WFLR_ADDRESS, abi: wnatAbi, functionName: 'deposit', args: [], value: amount }
            : { address: WFLR_ADDRESS, abi: wnatAbi, functionName: 'withdraw', args: [amount] }
        )
      } catch (error) {
        setPhase({ status: 'error', error: firstLine(error) })
        return
      }
      setPhase({ status: 'pending', hash })
      try {
        const receipt = await publicClient?.waitForTransactionReceipt({ hash })
        if (receipt && receipt.status === 'reverted') {
          setPhase({ status: 'error', error: new Error(`the ${direction} reverted on chain (${hash})`) })
          return
        }
        setPhase({ status: 'success', hash })
      } catch (error) {
        setPhase({ status: 'error', error: firstLine(error) })
      }
    },
    [address, publicClient, writeContractAsync]
  )

  const isBusy = phase.status === 'signing' || phase.status === 'pending'

  return { phase, isBusy, execute, reset: () => setPhase({ status: 'idle' }) }
}

function firstLine(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error)
  return new Error(message.split('\n')[0] ?? 'failed', { cause: error })
}
